import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <Link 
          to="/" 
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to home
        </Link>

        <h1 className="text-4xl font-bold mb-2">Privacy Policy</h1>
        <p className="text-xl font-semibold text-muted-foreground mb-8">Wololo Solutions</p>
        <p className="text-sm text-muted-foreground mb-12">Last updated: April 2026</p>

        <div className="mb-8 p-6 bg-muted/50 rounded-lg text-sm text-muted-foreground space-y-1">
          <p>
            This Privacy Policy explains how Wololo Property Solutions ("we," "our," or "us") collects, uses, stores, shares, and otherwise processes personal data in connection with the Wololo Solutions website, application, and related services.
          </p>
          <p className="mt-4">If you have questions about this Privacy Policy or want to exercise your privacy rights, you can contact us at:</p>
          <address className="mt-2 not-italic">
            <p className="font-medium text-foreground">Wololo Property Solutions</p>
            <p>BILIQ, SUNSET ROAD 819, BALI RUKO SUNSET INDAH II NOMOR 10,</p>
            <p>Desa/Kelurahan Seminyak, Kec. Kuta, Kab. Badung, Provinsi Bali,</p>
            <p>Kode Pos: 80361</p>
            <p>Privacy contact: <a href="mailto:wololo.property.solutions@gmail.com" className="text-primary hover:underline">wololo.property.solutions@gmail.com</a></p>
          </address>
        </div>

        <nav className="mb-12 p-6 bg-muted/50 rounded-lg">
          <h2 className="font-semibold mb-4">Table of contents</h2>
          <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
            <li><a href="#who-we-are" className="hover:text-foreground">Who we are</a></li>
            <li><a href="#what-this-covers" className="hover:text-foreground">What this Privacy Policy covers</a></li>
            <li><a href="#controller-processor" className="hover:text-foreground">Controller and processor roles</a></li>
            <li><a href="#data-we-collect" className="hover:text-foreground">Personal data we collect</a></li>
            <li><a href="#sources" className="hover:text-foreground">Sources of personal data</a></li>
            <li><a href="#how-we-use" className="hover:text-foreground">How we use personal data</a></li>
            <li><a href="#legal-bases" className="hover:text-foreground">Legal bases for processing</a></li>
            <li><a href="#cookies" className="hover:text-foreground">Cookies, local storage, and similar technologies</a></li>
            <li><a href="#sharing" className="hover:text-foreground">How we share personal data</a></li>
            <li><a href="#third-parties" className="hover:text-foreground">Third-party service providers</a></li>
            <li><a href="#international-transfers" className="hover:text-foreground">International transfers</a></li>
            <li><a href="#retention" className="hover:text-foreground">Data retention</a></li>
            <li><a href="#security" className="hover:text-foreground">Security</a></li>
            <li><a href="#your-rights" className="hover:text-foreground">Your rights</a></li>
            <li><a href="#eea-uk" className="hover:text-foreground">Additional information for the EEA and UK</a></li>
            <li><a href="#indonesia" className="hover:text-foreground">Additional information for Indonesia</a></li>
            <li><a href="#changes" className="hover:text-foreground">Changes to this Privacy Policy</a></li>
            <li><a href="#contact" className="hover:text-foreground">Contact us</a></li>
          </ol>
        </nav>

        <div className="prose prose-neutral dark:prose-invert max-w-none space-y-8">
          <section id="who-we-are">
            <h2 className="text-2xl font-semibold mb-4">1. Who we are</h2>
            <p className="text-muted-foreground mb-4">
              This Privacy Policy explains how Wololo Property Solutions ("we," "our," or "us") collects, uses, stores, shares, and otherwise processes personal data in connection with the Wololo Solutions website, application, and related services.
            </p>
            <p className="text-muted-foreground mb-4">
              For personal data relating to our website, account administration, subscriptions, billing, support, service communications, security, diagnostics, and our direct relationship with customers, we generally act as the controller or equivalent responsible party.
            </p>
            <p className="text-muted-foreground">
              Where a business customer uses Wololo Solutions to manage its own employees, contractors, or other personnel through the platform, that customer generally determines why and how that workforce data is processed. In those cases, the customer generally acts as the controller or equivalent responsible party, and we act as a processor or service provider on the customer's behalf, subject to applicable law and our agreements with that customer.
            </p>
          </section>

          <section id="what-this-covers">
            <h2 className="text-2xl font-semibold mb-4">2. What this Privacy Policy covers</h2>
            <p className="text-muted-foreground mb-2">This Privacy Policy applies to personal data processed in connection with:</p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1">
              <li>our website;</li>
              <li>the Wololo Solutions application;</li>
              <li>account creation, sign-in, and authentication;</li>
              <li>subscriptions, billing, and payment administration;</li>
              <li>support requests and service communications;</li>
              <li>security, diagnostics, troubleshooting, and product improvement;</li>
              <li>related business services that we provide through the platform.</li>
            </ul>
          </section>

          <section id="controller-processor">
            <h2 className="text-2xl font-semibold mb-4">3. Controller and processor roles</h2>
            <p className="text-muted-foreground mb-4">
              For personal data relating to our website, account administration, subscriptions, billing, support, service communications, security, diagnostics, and our direct relationship with customers, we generally act as the controller or equivalent responsible party.
            </p>
            <p className="text-muted-foreground mb-4">
              Where a business customer uses Wololo Solutions to manage its own employees, contractors, or other personnel through the platform, that customer generally determines why and how that workforce data is processed. In those cases, the customer generally acts as the controller or equivalent responsible party, and we act as a processor or service provider on the customer's behalf, subject to applicable law and our agreements with that customer.
            </p>
            <p className="text-muted-foreground">
              If you are an employee or other user invited into the platform by your organisation, your organisation may also provide its own privacy notice explaining how and why it uses the platform.
            </p>
          </section>

          <section id="data-we-collect">
            <h2 className="text-2xl font-semibold mb-4">4. Personal data we collect</h2>
            <p className="text-muted-foreground mb-6">
              The personal data processed through Wololo Solutions depends on how the platform is configured and how you interact with it.
            </p>

            <h3 className="text-lg font-medium mb-2">4.1 Account and profile data</h3>
            <p className="text-muted-foreground mb-2">
              This may include names, email addresses, phone numbers, job titles, account status, company membership, profile images, and related profile information.
            </p>
            <p className="text-muted-foreground mb-6">
              It may also include role assignments, permission settings, and access-control data used to determine what a user can see or do within the platform.
            </p>

            <h3 className="text-lg font-medium mb-2">4.2 Workforce operations data</h3>
            <p className="text-muted-foreground mb-6">
              Depending on the enabled features, the platform may process workforce data such as schedules, scheduled shifts, shift codes, roster information, time entries, clock-in and clock-out records, timesheet approvals, edit history, comments, attendance flags, missed-punch records, leave requests, leave balances, leave policy-related records, and accrual history.
            </p>

            <h3 className="text-lg font-medium mb-2">4.3 Geolocation and geofence data</h3>
            <p className="text-muted-foreground mb-2">
              If geolocation features are enabled by a customer, the platform may process geolocation-related data connected to attendance workflows, including latitude, longitude, location accuracy, time of capture, geofence identifiers or labels, distance from a site, whether a user was inside or outside an allowed radius, and related attendance flags or reasons.
            </p>
            <p className="text-muted-foreground mb-6">
              These features may be used for attendance verification, geofence enforcement, attendance review, and related notification workflows.
            </p>

            <h3 className="text-lg font-medium mb-2">4.4 Payroll and compensation data</h3>
            <p className="text-muted-foreground mb-6">
              Depending on how the platform is used, we may process payroll-related and compensation-related data, including payroll runs, payslips, payslip line items, compensation rules, compensation rule assignments, overtime calculations, deductions, taxes, service charge distributions, and year-to-date summaries.
            </p>

            <h3 className="text-lg font-medium mb-2">4.5 Billing and subscription data</h3>
            <p className="text-muted-foreground mb-2">
              If a customer subscribes to a paid plan, we may process billing and subscription data such as plan name, subscription status, trial status, included and billable seat counts, billing contact details, and payment-administration data.
            </p>
            <p className="text-muted-foreground mb-6">
              Payment card details are generally handled by our payment processor and are not stored by us in full.
            </p>

            <h3 className="text-lg font-medium mb-2">4.6 Notification and communication data</h3>
            <p className="text-muted-foreground mb-6">
              We may process notification records, notification preferences, in-app notifications, email delivery details, and delivery-status information where these features are used.
            </p>

            <h3 className="text-lg font-medium mb-2">4.7 Technical, device, and usage data</h3>
            <p className="text-muted-foreground mb-6">
              When you use the website or application, we may process technical and usage data such as IP address, device and browser information, login timestamps, pages viewed, actions taken, system events, and related usage activity.
            </p>

            <h3 className="text-lg font-medium mb-2">4.8 Diagnostics and replay data</h3>
            <p className="text-muted-foreground mb-2">
              We use technical diagnostics tools to detect errors, analyse performance, and improve service reliability. Where enabled, this may include error monitoring, performance tracing, and session replay or similar debugging tools.
            </p>
            <p className="text-muted-foreground">
              These tools are used to help investigate technical issues and improve the service. Where required by law, we will seek consent before using non-essential diagnostics or replay technologies.
            </p>
          </section>

          <section id="sources">
            <h2 className="text-2xl font-semibold mb-4">5. Sources of personal data</h2>
            <p className="text-muted-foreground mb-2">We may collect personal data:</p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1">
              <li>directly from you;</li>
              <li>from your employer, organisation, or account administrator;</li>
              <li>from your use of the Wololo Solutions website or application;</li>
              <li>from service providers supporting hosting, billing, security, or diagnostics;</li>
              <li>from account, authentication, billing, and platform-configuration workflows.</li>
            </ul>
          </section>

          <section id="how-we-use">
            <h2 className="text-2xl font-semibold mb-4">6. How we use personal data</h2>
            <p className="text-muted-foreground mb-2">We use personal data for purposes such as:</p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1">
              <li>creating and managing accounts;</li>
              <li>authenticating users and maintaining secure sessions;</li>
              <li>providing platform functionality;</li>
              <li>managing scheduling, attendance, leave, payroll-related records, and notifications;</li>
              <li>supporting geolocation and geofencing attendance controls where enabled;</li>
              <li>processing subscriptions, trials, billing, and related administration;</li>
              <li>responding to support requests and service enquiries;</li>
              <li>maintaining security, detecting misuse, and investigating incidents;</li>
              <li>generating audit trails, operational records, and history views;</li>
              <li>monitoring and improving service reliability, performance, and usability;</li>
              <li>complying with legal obligations;</li>
              <li>enforcing agreements and protecting our rights.</li>
            </ul>
          </section>

          <section id="legal-bases">
            <h2 className="text-2xl font-semibold mb-4">7. Legal bases for processing</h2>
            <p className="text-muted-foreground mb-2">Where GDPR or similar laws apply, our legal bases may include:</p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 mb-4">
              <li>performance of a contract;</li>
              <li>compliance with legal obligations;</li>
              <li>legitimate interests;</li>
              <li>consent, where consent is required.</li>
            </ul>
            <p className="text-muted-foreground">
              Where we act on behalf of a customer as processor or service provider, the customer is generally responsible for determining the lawful basis for the workforce data it instructs us to process through the platform.
            </p>
          </section>

          <section id="cookies">
            <h2 className="text-2xl font-semibold mb-4">8. Cookies, local storage, and similar technologies</h2>
            <p className="text-muted-foreground mb-4">
              We use cookies, local storage, and similar technologies for several purposes.
            </p>

            <h3 className="text-lg font-medium mb-2">8.1 Essential technologies</h3>
            <p className="text-muted-foreground mb-4">
              These may be used to keep users signed in, maintain authentication or session state, operate core app features, and support security-related functions.
            </p>

            <h3 className="text-lg font-medium mb-2">8.2 Preference technologies</h3>
            <p className="text-muted-foreground mb-4">
              These may be used to remember user choices such as appearance preferences, interface state, and similar usability-related settings.
            </p>

            <h3 className="text-lg font-medium mb-2">8.3 Diagnostics and performance technologies</h3>
            <p className="text-muted-foreground mb-4">
              These may be used to monitor errors, understand service performance, diagnose failures, and improve reliability and usability.
            </p>

            <h3 className="text-lg font-medium mb-2">8.4 Replay technologies</h3>
            <p className="text-muted-foreground">
              Where enabled, replay technologies may be used to help investigate technical issues or user-reported bugs. Where required by applicable law, we will ask for consent before using non-essential cookies or similar technologies and before enabling non-essential diagnostics or replay features.
            </p>
          </section>

          <section id="sharing">
            <h2 className="text-2xl font-semibold mb-4">9. How we share personal data</h2>
            <p className="text-muted-foreground mb-2">We may share personal data:</p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 mb-4">
              <li>with service providers that help us operate the platform;</li>
              <li>with payment processors for subscription and billing administration;</li>
              <li>with infrastructure, hosting, security, and diagnostics providers;</li>
              <li>with professional advisers where reasonably necessary;</li>
              <li>with regulators, courts, law enforcement, or authorities where legally required;</li>
              <li>in connection with a merger, acquisition, restructuring, financing, or sale of business assets.</li>
            </ul>
            <p className="text-muted-foreground font-medium">We do not sell personal data.</p>
          </section>

          <section id="third-parties">
            <h2 className="text-2xl font-semibold mb-4">10. Third-party service providers</h2>
            <p className="text-muted-foreground mb-2">Based on the current platform setup, service providers used in support of the service include:</p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1">
              <li>Supabase for backend and infrastructure-related services;</li>
              <li>Stripe for subscription and billing processing;</li>
              <li>Sentry for diagnostics, error monitoring, performance tracing, and potentially replay tooling.</li>
            </ul>
          </section>

          <section id="international-transfers">
            <h2 className="text-2xl font-semibold mb-4">11. International transfers</h2>
            <p className="text-muted-foreground mb-4">
              Personal data may be processed in countries other than the country where the relevant user or customer is located.
            </p>
            <p className="text-muted-foreground">
              Where required by law, we use appropriate safeguards for international transfers, which may include contractual safeguards, lawful transfer mechanisms, and supplementary technical or organisational protections where appropriate.
            </p>
          </section>

          <section id="retention">
            <h2 className="text-2xl font-semibold mb-4">12. Data retention</h2>
            <p className="text-muted-foreground mb-2">
              We retain personal data for as long as reasonably necessary for the purposes described in this Privacy Policy, including to:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 mb-4">
              <li>provide the service;</li>
              <li>maintain security and business continuity;</li>
              <li>comply with legal, tax, payroll, accounting, or regulatory obligations;</li>
              <li>resolve disputes;</li>
              <li>enforce agreements;</li>
              <li>support customer instructions where we act as processor or service provider.</li>
            </ul>
            <p className="text-muted-foreground">
              Retention periods may vary depending on the category of data, such as account and subscription data, billing records, timesheets and attendance records, leave records, payroll and payslip records, geolocation logs, diagnostics logs, and audit/history records.
            </p>
          </section>

          <section id="security">
            <h2 className="text-2xl font-semibold mb-4">13. Security</h2>
            <p className="text-muted-foreground">
              We use technical and organisational measures designed to protect personal data against unauthorised access, loss, misuse, alteration, or disclosure. No system can be guaranteed to be completely secure, but we take security seriously and work to protect the confidentiality, integrity, and availability of the data we process.
            </p>
          </section>

          <section id="your-rights">
            <h2 className="text-2xl font-semibold mb-4">14. Your rights</h2>
            <p className="text-muted-foreground mb-2">Depending on your location and the applicable law, you may have rights such as:</p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 mb-4">
              <li>the right to access personal data;</li>
              <li>the right to correct inaccurate or incomplete personal data;</li>
              <li>the right to request deletion of personal data;</li>
              <li>the right to request restriction of processing;</li>
              <li>the right to object to certain processing;</li>
              <li>the right to data portability;</li>
              <li>the right to withdraw consent, where processing is based on consent;</li>
              <li>the right to lodge a complaint with a supervisory or regulatory authority.</li>
            </ul>
            <p className="text-muted-foreground">
              You can exercise applicable rights by contacting us using the contact details in this Privacy Policy. If we are processing your data on behalf of your employer or another organisation, we may direct you to that organisation where appropriate.
            </p>
          </section>

          <section id="eea-uk">
            <h2 className="text-2xl font-semibold mb-4">15. Additional information for the EEA and UK</h2>
            <p className="text-muted-foreground mb-4">
              If you are in the EEA or UK, you may have additional rights under GDPR or related data protection laws.
            </p>
            <p className="text-muted-foreground mb-2">Where we act as controller for relevant data, our legal bases may include:</p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 mb-4">
              <li>contractual necessity;</li>
              <li>legitimate interests;</li>
              <li>legal obligations;</li>
              <li>consent, where required.</li>
            </ul>
            <p className="text-muted-foreground">
              You may also have the right to lodge a complaint with your local supervisory authority.
            </p>
          </section>

          <section id="indonesia">
            <h2 className="text-2xl font-semibold mb-4">16. Additional information for Indonesia</h2>
            <p className="text-muted-foreground mb-4">
              If Indonesian personal data protection law applies, including Law No. 27 of 2022 on Personal Data Protection and related regulations, individuals may have rights under applicable law, including rights to obtain information about the processing of their personal data, access personal data, request correction or updating, withdraw consent where consent is the legal basis used, request deletion, destruction, or limitation of processing where applicable under law, and submit complaints in accordance with applicable law.
            </p>
            <p className="text-muted-foreground">
              Where we process workforce data on behalf of a business customer, that customer is generally responsible for determining the legal basis and workplace-compliance basis for that processing.
            </p>
          </section>

          <section id="changes">
            <h2 className="text-2xl font-semibold mb-4">17. Changes to this Privacy Policy</h2>
            <p className="text-muted-foreground">
              We may update this Privacy Policy from time to time. When we do, we may update the "Last updated" date above and, where appropriate, provide additional notice through the website, application, or other suitable means.
            </p>
          </section>

          <section id="contact">
            <h2 className="text-2xl font-semibold mb-4">18. Contact us</h2>
            <address className="not-italic text-muted-foreground space-y-1">
              <p className="font-medium text-foreground">Wololo Property Solutions</p>
              <p>BILIQ, SUNSET ROAD 819, BALI RUKO SUNSET INDAH II NOMOR 10,</p>
              <p>Desa/Kelurahan Seminyak, Kec. Kuta, Kab. Badung, Provinsi Bali,</p>
              <p>Kode Pos: 80361</p>
              <p>Email: <a href="mailto:wololo.property.solutions@gmail.com" className="text-primary hover:underline">wololo.property.solutions@gmail.com</a></p>
            </address>
          </section>
        </div>

        <div className="mt-16 pt-8 border-t text-center text-sm text-muted-foreground">
          <p>© {new Date().getFullYear()} Wololo Property Solutions. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;